import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Car, Save, Check, Plus, Trash2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API } from '../../utils/apiConfig';

export const AdminVehicleSegmentsTab = () => {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState('prices'); // 'prices' | 'mapping'
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [message, setMessage] = useState(null);

  // Mapping state
  const [mappings, setMappings] = useState([]);
  const [mapSearch, setMapSearch] = useState('');
  const [mapLoading, setMapLoading] = useState(false);
  const [newMapping, setNewMapping] = useState({ make: '', model: '', segment_key: '' });
  const [addingMapping, setAddingMapping] = useState(false);

  const loadSegments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/vehicle-segments`);
      setSegments(res.data.segments || []);
    } catch (e) {
      setMessage({ type: 'error', text: t('common.error') });
    }
    setLoading(false);
  }, [t]);

  const loadMappings = useCallback(async () => {
    setMapLoading(true);
    try {
      const res = await axios.get(`${API}/admin/segment-mappings`, {
        params: mapSearch ? { search: mapSearch } : {},
      });
      setMappings(res.data.mappings || []);
    } catch (e) {
      setMessage({ type: 'error', text: t('common.error') });
    }
    setMapLoading(false);
  }, [mapSearch, t]);

  useEffect(() => { loadSegments(); }, [loadSegments]);
  useEffect(() => {
    if (subTab === 'mapping') loadMappings();
  }, [subTab, loadMappings]);

  const updateField = (key, field, value) => {
    setSegments(prev => prev.map(s => s.segment_key === key ? { ...s, [field]: value } : s));
  };

  const saveSegment = async (seg) => {
    setSavingKey(seg.segment_key);
    setMessage(null);
    try {
      const res = await axios.put(`${API}/admin/vehicle-segments/${seg.segment_key}`, {
        preis_km: parseFloat(seg.preis_km),
        preis_km_long: parseFloat(seg.preis_km_long),
      });
      const count = res.data.updated_vehicles_count || 0;
      const className = t(`admin.kba.classes.${seg.segment_key}`, seg.segment_name);
      setMessage({
        type: 'success',
        text: `${className} · ${t('admin.kba.pricesSaved')}${count > 0 ? ' · ' + t('admin.kba.vehiclesUpdated', { count }) : ''}`,
      });
      setTimeout(() => setMessage(null), 4000);
    } catch (e) {
      const code = e.response?.data?.detail;
      const key = `auth.errors.${code}`;
      const tx = t(key);
      setMessage({ type: 'error', text: tx !== key ? tx : (code || t('common.error')) });
    }
    setSavingKey(null);
  };

  const addMapping = async (e) => {
    e?.preventDefault();
    if (!newMapping.make || !newMapping.model || !newMapping.segment_key) return;
    setAddingMapping(true);
    setMessage(null);
    try {
      await axios.post(`${API}/admin/segment-mappings`, {
        make: newMapping.make.trim().toLowerCase(),
        model: newMapping.model.trim().toLowerCase(),
        segment_key: newMapping.segment_key,
      });
      setNewMapping({ make: '', model: '', segment_key: '' });
      await loadMappings();
      setMessage({ type: 'success', text: t('admin.kba.saved') });
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      const code = err.response?.data?.detail;
      setMessage({ type: 'error', text: code || t('common.error') });
    }
    setAddingMapping(false);
  };

  const deleteMapping = async (id) => {
    if (!window.confirm(t('admin.kba.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/admin/segment-mappings/${id}`);
      setMappings(prev => prev.filter(m => m.id !== id));
    } catch (e) {
      setMessage({ type: 'error', text: t('common.error') });
    }
  };

  const updateMappingSegment = async (id, segment_key) => {
    try {
      await axios.put(`${API}/admin/segment-mappings/${id}`, { segment_key });
      setMappings(prev => prev.map(m => m.id === id ? { ...m, segment_key } : m));
    } catch (e) {
      setMessage({ type: 'error', text: t('common.error') });
    }
  };

  return (
    <div className="settings-content" data-testid="admin-kba-tab">
      <div className="settings-card">
        <div className="settings-header">
          <Car size={32} />
          <h2>{t('admin.kba.kbaTitle')}</h2>
          <p>{t('admin.kba.kbaDescription')}</p>
        </div>

        {/* Sub-tab switcher */}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={() => setSubTab('prices')}
            data-testid="kba-subtab-prices"
            style={subTabStyle(subTab === 'prices')}
          >
            {t('admin.kba.pricesSubtab')}
          </button>
          <button
            onClick={() => setSubTab('mapping')}
            data-testid="kba-subtab-mapping"
            style={subTabStyle(subTab === 'mapping')}
          >
            {t('admin.kba.mappingSubtab')}
          </button>
        </div>

        {message && (<div className={`message-box ${message.type}`}>{message.text}</div>)}

        {/* PRICES sub-tab */}
        {subTab === 'prices' && (
          <>
            {loading && <div style={{ padding: 20, color: '#94a3b8' }}>{t('common.loading')}</div>}
            {!loading && segments.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle} data-testid="kba-segments-table">
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('admin.kba.class')}</th>
                      <th style={thStyle}>Beispiele</th>
                      <th style={thStyle}>€/km (kurz)</th>
                      <th style={thStyle}>€/km (&gt;100km)</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map(seg => (
                      <tr key={seg.segment_key} data-testid={`kba-row-${seg.segment_key}`}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 700 }}>{t(`admin.kba.classes.${seg.segment_key}`, seg.segment_name)}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{seg.segment_key}</div>
                        </td>
                        <td style={{ ...tdStyle, fontSize: 12, color: '#94a3b8', maxWidth: 260 }}>
                          {seg.example_cars}
                        </td>
                        <td style={tdStyle}>
                          <input type="number" step="0.001" min="0" max="5"
                            value={seg.preis_km ?? ''}
                            onChange={e => updateField(seg.segment_key, 'preis_km', e.target.value)}
                            data-testid={`kba-preis-km-${seg.segment_key}`}
                            style={numberInputStyle} />
                        </td>
                        <td style={tdStyle}>
                          <input type="number" step="0.001" min="0" max="5"
                            value={seg.preis_km_long ?? ''}
                            onChange={e => updateField(seg.segment_key, 'preis_km_long', e.target.value)}
                            data-testid={`kba-preis-km-long-${seg.segment_key}`}
                            style={numberInputStyle} />
                        </td>
                        <td style={tdStyle}>
                          <button onClick={() => saveSegment(seg)} disabled={savingKey === seg.segment_key}
                            data-testid={`kba-save-${seg.segment_key}`} style={saveBtnStyle}>
                            <Save size={14} />
                            {savingKey === seg.segment_key ? '...' : t('common.save')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* MAPPING sub-tab */}
        {subTab === 'mapping' && (
          <>
            <form onSubmit={addMapping} style={addFormStyle} data-testid="kba-mapping-add-form">
              <input type="text" placeholder={t('admin.kba.make')}
                value={newMapping.make}
                onChange={e => setNewMapping({ ...newMapping, make: e.target.value })}
                data-testid="kba-mapping-new-make" style={mapInputStyle} />
              <input type="text" placeholder={t('admin.kba.model')}
                value={newMapping.model}
                onChange={e => setNewMapping({ ...newMapping, model: e.target.value })}
                data-testid="kba-mapping-new-model" style={mapInputStyle} />
              <select value={newMapping.segment_key}
                onChange={e => setNewMapping({ ...newMapping, segment_key: e.target.value })}
                data-testid="kba-mapping-new-segment" style={mapInputStyle}>
                <option value="">– {t('admin.kba.class')} –</option>
                {segments.map(s => <option key={s.segment_key} value={s.segment_key}>{s.segment_name}</option>)}
              </select>
              <button type="submit" disabled={addingMapping || !newMapping.make || !newMapping.model || !newMapping.segment_key}
                data-testid="kba-mapping-add-btn" style={addBtnStyle}>
                <Plus size={14} /> {t('admin.kba.addMapping')}
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
              <Search size={16} color="#94a3b8" />
              <input type="text" placeholder={t('admin.kba.search')}
                value={mapSearch} onChange={e => setMapSearch(e.target.value)}
                data-testid="kba-mapping-search" style={{ ...mapInputStyle, flex: 1 }} />
            </div>

            {mapLoading && <div style={{ padding: 20, color: '#94a3b8' }}>{t('common.loading')}</div>}

            {!mapLoading && mappings.length === 0 && (
              <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>{t('admin.kba.noResults')}</div>
            )}

            {!mapLoading && mappings.length > 0 && (
              <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
                <table style={tableStyle} data-testid="kba-mapping-table">
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('admin.kba.make')}</th>
                      <th style={thStyle}>{t('admin.kba.model')}</th>
                      <th style={thStyle}>{t('admin.kba.class')}</th>
                      <th style={thStyle}>{t('admin.kba.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map(m => (
                      <tr key={m.id} data-testid={`kba-mapping-row-${m.id}`}>
                        <td style={tdStyle}>{m.make}</td>
                        <td style={tdStyle}>{m.model}</td>
                        <td style={tdStyle}>
                          <select value={m.segment_key}
                            onChange={e => updateMappingSegment(m.id, e.target.value)}
                            data-testid={`kba-mapping-segment-${m.id}`} style={mapInputStyle}>
                            {segments.map(s => <option key={s.segment_key} value={s.segment_key}>{t(`admin.kba.classes.${s.segment_key}`, s.segment_name)}</option>)}
                          </select>
                        </td>
                        <td style={tdStyle}>
                          <button onClick={() => deleteMapping(m.id)}
                            data-testid={`kba-mapping-delete-${m.id}`} style={deleteBtnStyle}
                            title={t('common.delete')}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const subTabStyle = (active) => ({
  padding: '10px 18px', borderRadius: '8px 8px 0 0', cursor: 'pointer',
  background: active ? 'rgba(0,212,255,0.15)' : 'transparent',
  color: active ? '#00d4ff' : '#94a3b8',
  border: 'none', borderBottom: active ? '2px solid #00d4ff' : '2px solid transparent',
  fontSize: 14, fontWeight: 600, marginBottom: -1,
});
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = {
  textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)',
  color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
};
const tdStyle = { padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#f1f5f9' };
const numberInputStyle = {
  width: 100, padding: '8px 10px', borderRadius: 8,
  background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(167,139,250,0.4)',
  color: '#ffffff', outline: 'none', fontSize: 14, fontWeight: 700, textAlign: 'right',
};
const saveBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
  background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)', color: '#fff',
  border: 'none', fontSize: 12, fontWeight: 600,
};
const addFormStyle = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const mapInputStyle = {
  padding: '8px 10px', borderRadius: 8,
  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
  color: '#f1f5f9', outline: 'none', fontSize: 13,
};
const addBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8,
  background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff',
  border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const deleteBtnStyle = {
  padding: '6px 10px', borderRadius: 6,
  background: 'rgba(239,68,68,0.15)', color: '#ef4444',
  border: '1px solid rgba(239,68,68,0.3)', cursor: 'pointer',
};
